/**
 * Custom error types for Power Automate MCP
 */

export class PowerAutomateError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode?: number,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "PowerAutomateError";
    Error.captureStackTrace(this, this.constructor);
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      details: sanitizeErrorDetails(this.details),
    };
  }
}

export class AuthenticationError extends PowerAutomateError {
  constructor(message: string, details?: unknown) {
    super(message, "AUTH_ERROR", 401, details);
    this.name = "AuthenticationError";
  }
}

export class AuthorizationError extends PowerAutomateError {
  constructor(message: string, details?: unknown) {
    super(message, "AUTHZ_ERROR", 403, details);
    this.name = "AuthorizationError";
  }
}

/**
 * Why Entra refused a silent token renewal, when the error says so.
 *
 * Every cause here requires interactive sign-in to resolve — no client code
 * can silently recover any of them — but they mean very different things to
 * the person reading the error, so naming the cause turns "it randomly
 * dropped again" into a diagnosis:
 *
 * - AADSTS70043 / 50076 / 50079 — the organization's Conditional Access
 *   policy (sign-in frequency or MFA re-auth) expired the session on
 *   schedule. Expected on managed tenants; recurring by design.
 * - AADSTS50173 — the sign-in was revoked (password change/security event).
 * - AADSTS700082 — the refresh token expired after 90 days without use.
 * - AADSTS53003 — blocked outright by Conditional Access (often a
 *   compliant-device or trusted-network requirement); signing in again may
 *   NOT fix this one, so it gets a distinct "talk to IT" message.
 */
export type SilentReauthCause = "policy-reauth" | "revoked" | "idle-expiry" | "ca-blocked" | null;

export function classifySilentReauthCause(msg: string): SilentReauthCause {
  if (/AADSTS(70043|50076|50079)/.test(msg)) return "policy-reauth";
  if (/AADSTS50173/.test(msg)) return "revoked";
  if (/AADSTS700082/.test(msg)) return "idle-expiry";
  if (/AADSTS53003/.test(msg)) return "ca-blocked";
  return null;
}

/** Trim Entra correlation noise (Trace/Correlation IDs, timestamps) off an AADSTS message. */
function trimEntraError(msg: string): string {
  return msg.split(" Trace ID")[0]?.trim() ?? msg;
}

/**
 * Did a silent token acquisition fail because Microsoft explicitly demands
 * user interaction (consent, MFA, policy re-auth)? Those failures can ONLY
 * be satisfied by an interactive flow — throwing instead sends the user to
 * dead ends. Detected structurally (MSAL's InteractionRequiredAuthError name
 * / errorCode) and by AADSTS code, WITHOUT importing the MSAL class: several
 * test files mock @azure/msal-node with only PublicClientApplication, and an
 * `instanceof undefined` check throws.
 */
export function isInteractionRequiredError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; errorCode?: string; message?: string };
  if (e.name === "InteractionRequiredAuthError") return true;
  if (
    typeof e.errorCode === "string" &&
    /^(interaction_required|consent_required|login_required|claims_challenge)/.test(e.errorCode)
  ) {
    return true;
  }
  const msg = typeof e.message === "string" ? e.message : "";
  if (/interaction_required|consent_required|login_required/i.test(msg)) return true;
  if (/AADSTS(65001|50076|50079|70043|50173|700082)/.test(msg)) return true;
  return false;
}

const SIGN_IN_REMEDY =
  "Ask your AI assistant to run the sign_in tool, or run 'powerautomate-mcp --login' from a terminal.";

/**
 * Build the AuthenticationError for a failed silent-only token acquisition.
 *
 * A silent failure usually means no cached credentials — but AADSTS65001
 * means the app registration was never consented for the requested resource
 * (e.g. the optional PowerApps/BAP permission), and several other AADSTS
 * codes mean the credentials WERE cached and Entra refused to renew them
 * (tenant policy, revocation, idle expiry). Each of those sends the user in
 * a different direction, so surface the real cause instead of a blanket
 * "no cached credentials".
 */
export function silentOnlyAuthError(
  err: unknown,
  scopes: readonly string[]
): AuthenticationError {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (msg.includes("AADSTS65001")) {
    return new AuthenticationError(
      `Consent missing for ${scopes.join(", ")}: the app registration has not been granted (or an admin has not consented to) this permission. ` +
        `Add it in Entra ID and grant admin consent (see the App Registration table in the README) — re-running --setup will not fix this. ` +
        `Original error: ${trimEntraError(msg)}`
    );
  }

  switch (classifySilentReauthCause(msg)) {
    case "policy-reauth":
      return new AuthenticationError(
        "Sign-in expired: your organization's sign-in policy requires signing in again " +
          "(Conditional Access sign-in frequency or multi-factor re-authentication). " +
          "This recurs on your organization's schedule and is not an error in this tool. " +
          `${SIGN_IN_REMEDY} Original error: ${trimEntraError(msg)}`
      );
    case "revoked":
      return new AuthenticationError(
        "Sign-in revoked: your cached sign-in is no longer valid — usually a password change " +
          "or a security event on the account. " +
          `${SIGN_IN_REMEDY} Original error: ${trimEntraError(msg)}`
      );
    case "idle-expiry":
      return new AuthenticationError(
        "Sign-in expired after 90 days without use. " +
          `${SIGN_IN_REMEDY} Original error: ${trimEntraError(msg)}`
      );
    case "ca-blocked":
      return new AuthenticationError(
        "Access blocked by your organization's Conditional Access policy — often a " +
          "compliant/managed-device or trusted-network requirement. Signing in again may not fix " +
          "this; if it persists, your IT admin needs to allow this app from this device or network. " +
          `Original error: ${trimEntraError(msg)}`
      );
    case null:
      break;
  }

  return new AuthenticationError(
    "No cached credentials available. Ask your AI assistant to run the sign_in tool, " +
    "or run 'powerautomate-mcp --login' (first-time setup: 'powerautomate-mcp --setup') from a terminal."
  );
}

export class FlowNotFoundError extends PowerAutomateError {
  constructor(flowId: string) {
    super(`Flow not found: ${flowId}`, "FLOW_NOT_FOUND", 404);
    this.name = "FlowNotFoundError";
  }
}

export class EnvironmentNotFoundError extends PowerAutomateError {
  constructor(environmentId: string) {
    super(`Environment not found: ${environmentId}`, "ENV_NOT_FOUND", 404);
    this.name = "EnvironmentNotFoundError";
  }
}

export class ConnectorNotFoundError extends PowerAutomateError {
  constructor(connectorId: string) {
    super(`Connector not found: ${connectorId}`, "CONNECTOR_NOT_FOUND", 404);
    this.name = "ConnectorNotFoundError";
  }
}

export class ValidationError extends PowerAutomateError {
  constructor(message: string, details?: unknown) {
    super(message, "VALIDATION_ERROR", 400, details);
    this.name = "ValidationError";
  }
}

/**
 * A destructive tool was called without `confirm: true`, so it did nothing.
 *
 * This is thrown rather than returned so the call is reported with
 * `isError: true`. Returning the prompt as ordinary text made a refusal
 * indistinguishable from success: a scripted caller that deletes 59 items and
 * checks only `isError` reported 59 deletions and performed none.
 *
 * The message is the same prompt as before and is rendered verbatim, so the
 * interactive flow is unchanged — read it, re-call with `confirm: true`.
 */
export class ConfirmationRequiredError extends PowerAutomateError {
  constructor(message: string) {
    super(message, "CONFIRMATION_REQUIRED", 400);
    this.name = "ConfirmationRequiredError";
  }
}

export class RateLimitError extends PowerAutomateError {
  constructor(retryAfterMs?: number) {
    super(
      `Rate limit exceeded${retryAfterMs ? `. Retry after ${retryAfterMs}ms` : ""}`,
      "RATE_LIMIT",
      429,
      { retryAfterMs }
    );
    this.name = "RateLimitError";
  }
}

export class ConfigurationError extends PowerAutomateError {
  constructor(message: string, details?: unknown) {
    super(message, "CONFIG_ERROR", undefined, details);
    this.name = "ConfigurationError";
  }
}

export class PlatformError extends PowerAutomateError {
  constructor(message: string) {
    super(message, "PLATFORM_ERROR");
    this.name = "PlatformError";
  }
}

export class ApiError extends PowerAutomateError {
  constructor(
    message: string,
    statusCode: number,
    details?: unknown
  ) {
    super(message, "API_ERROR", statusCode, details);
    this.name = "ApiError";
  }
}

/**
 * Sanitize error details to prevent leaking sensitive information
 * (tokens, stack traces, internal paths, credentials) in serialized errors.
 */
function sanitizeErrorDetails(details: unknown): unknown {
  if (details == null) return undefined;

  // If it's an Error object, extract only safe fields
  if (details instanceof Error) {
    return {
      message: details.message,
      name: details.name,
    };
  }

  // If it's a string, return as-is (no structured data to leak)
  if (typeof details === "string") return details;

  // If it's a number or boolean, return as-is
  if (typeof details !== "object") return details;

  // For objects/arrays, do a shallow sanitization — strip known sensitive keys
  // All lowercase — keys are lowercased before lookup
  const SENSITIVE_KEYS = new Set([
    "stack", "accesstoken", "token", "refreshtoken", "id_token",
    "password", "secret", "credential", "authorization", "cookie",
    "bearer_token", "access_token", "api_key", "client_secret",
  ]);

  if (Array.isArray(details)) {
    return details.map(sanitizeErrorDetails);
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(details as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      sanitized[key] = "[REDACTED]";
    } else {
      sanitized[key] = typeof value === "object" && value !== null
        ? sanitizeErrorDetails(value)
        : value;
    }
  }
  return sanitized;
}

/**
 * Type guard to check if an error is a PowerAutomateError
 */
export function isPowerAutomateError(error: unknown): error is PowerAutomateError {
  return error instanceof PowerAutomateError;
}

/**
 * Wrap an async function to convert unknown errors to PowerAutomateError
 */
export async function wrapApiCall<T>(
  fn: () => Promise<T>,
  context?: string
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (isPowerAutomateError(error)) {
      throw error;
    }

    const message = error instanceof Error
      ? error.message
      : "Unknown error occurred";

    throw new ApiError(
      context ? `${context}: ${message}` : message,
      500,
      error
    );
  }
}
